import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Input } from "reactstrap";
import { getInferenceMachine, getInferenceMachines } from "../../../../services/inferenceMachineService";

/**
 * Same UX as the legacy ChannelForm inference-machine field:
 *  - one input that displays the currently assigned hostname
 *  - clicking it opens a dropdown list
 *  - typing triggers a debounced server-side search (?search=)
 *  - clicking a result writes its id back via onChange
 *
 * Unlike the legacy form we don't get an inlined `inference_machine_data` from
 * the API, so we fetch the assigned machine by id once at mount to resolve its
 * hostname; this is one extra request per camera, not per render.
 */

const PLACEHOLDER = "Not assigned";
const DEBOUNCE_MS = 200;
const RESULTS_LIMIT = 50;

function machineHostname(machine) {
  if (!machine) return "";
  return machine.machine?.hostname || machine.hostname || `#${machine.id}`;
}

export default function InferenceMachineAutocomplete({ value, onChange }) {
  const [label, setLabel] = useState("");
  const [query, setQuery] = useState("");
  const [showList, setShowList] = useState(false);
  const [results, setResults] = useState([]);
  const debounceRef = useRef(null);
  // Monotonic ids used to discard responses that arrive out-of-order: only the
  // most recent search/resolve result is allowed to update state.
  const searchSeqRef = useRef(0);
  const resolveSeqRef = useRef(0);
  // Flipped on unmount so pending promises don't call setState on a dead component.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  // Resolve the currently assigned machine's hostname (once per id change).
  useEffect(() => {
    if (value == null) {
      setLabel("");
      return undefined;
    }
    resolveSeqRef.current += 1;
    const mySeq = resolveSeqRef.current;
    getInferenceMachine(value)
      .then((resp) => {
        if (!isMountedRef.current || mySeq !== resolveSeqRef.current) return;
        setLabel(machineHostname(resp?.data));
      })
      .catch(() => {
        if (!isMountedRef.current || mySeq !== resolveSeqRef.current) return;
        setLabel(`#${value}`);
      });
    return undefined;
  }, [value]);

  const runSearch = useCallback(async (q) => {
    searchSeqRef.current += 1;
    const mySeq = searchSeqRef.current;
    try {
      const { data } = await getInferenceMachines(1, q);
      if (!isMountedRef.current || mySeq !== searchSeqRef.current) return;
      const rows = data?.results || [];
      setResults(rows.slice(0, RESULTS_LIMIT));
    } catch (_e) {
      if (!isMountedRef.current || mySeq !== searchSeqRef.current) return;
      setResults([]);
    }
  }, []);

  const onInputChange = (e) => {
    const next = e.target.value;
    setQuery(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(next), DEBOUNCE_MS);
  };

  const onFocus = () => {
    setShowList(true);
    if (results.length === 0) runSearch("");
  };

  const onBlur = () => {
    // Delay so click on a result still registers before we hide the list.
    setTimeout(() => setShowList(false), 150);
  };

  const onPick = (machine) => {
    if (!machine) {
      onChange(null);
      setLabel("");
    } else {
      // The hostname travels with the id: a caller that needs to *route* to the machine (a probe)
      // would otherwise have to re-fetch what this component just resolved.
      onChange(machine.id, machineHostname(machine));
      setLabel(machineHostname(machine));
    }
    setQuery("");
    setShowList(false);
  };

  const displayed = showList ? query : label;

  return (
    <div style={{ position: "relative" }}>
      <Input
        bsSize="sm"
        value={displayed}
        placeholder={label || PLACEHOLDER}
        onChange={onInputChange}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{ fontWeight: showList ? "normal" : "bold" }}
      />
      {showList && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 50,
            background: "#1a1f2b",
            border: "1px solid #444",
            borderRadius: 4,
            maxHeight: 220,
            overflowY: "auto",
            marginTop: 2,
          }}
        >
          <button
            type="button"
            onMouseDown={() => onPick(null)}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              padding: "4px 8px",
              background: "transparent",
              color: "#aaa",
              border: "none",
              fontStyle: "italic",
            }}
          >
            — Not assigned —
          </button>
          {results.map((m) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={() => onPick(m)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "4px 8px",
                background: "transparent",
                color: "#eee",
                border: "none",
              }}
            >
              {machineHostname(m)}
            </button>
          ))}
          {results.length === 0 && query !== "" && (
            <div style={{ padding: "4px 8px", color: "#888", fontStyle: "italic" }}>
              No match for &quot;{query}&quot;
            </div>
          )}
        </div>
      )}
    </div>
  );
}

InferenceMachineAutocomplete.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

InferenceMachineAutocomplete.defaultProps = { value: null };
